// frontend/src/firebase/config.js
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  setPersistence, 
  browserLocalPersistence,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { apiCall } from '../services/api';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Configure Google Provider
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Set persistence to LOCAL
export const setupPersistence = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    return true;
  } catch (err) {
    console.error('Error setting persistence:', err);
    return false;
  }
};

// Call persistence setup immediately
setupPersistence();

export const signInWithGoogle = async () => {
  try {
    // Ensure persistence is set to LOCAL
    await setupPersistence();
    
    const result = await signInWithPopup(auth, googleProvider);
    
    // After successful Google sign-in, save user to backend
    try {
      const response = await apiCall('/api/auth/google', {
        method: 'POST',
        body: JSON.stringify({
          email: result.user.email,
          uid: result.user.uid,
          displayName: result.user.displayName
        }),
      });
      
      if (!response.ok) {
        console.warn('Warning: Failed to register Google user with backend');
      }
    } catch (backendError) {
      console.warn('Warning: Backend registration error', backendError);
      // We still want to proceed with login even if backend registration fails
    }
    
    return result;
  } catch (error) {
    console.error('Google Sign In Error:', error);
    throw error;
  }
};

// Email Password Authentication Functions
export const signUpWithEmailPassword = async (email, password, displayName) => {
  try {
    await setupPersistence();
    
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // After successful email sign-up, save user to backend
    try {
      const response = await apiCall('/api/auth/email-password', {
        method: 'POST',
        body: JSON.stringify({
          email: result.user.email,
          uid: result.user.uid,
          displayName: displayName || email.split('@')[0]
        }),
      });
      
      if (!response.ok) {
        console.warn('Warning: Failed to register email user with backend');
      }
    } catch (backendError) {
      console.warn('Warning: Backend registration error', backendError);
    }
    
    return result;
  } catch (error) {
    console.error('Email Sign Up Error:', error);
    throw error;
  }
};

export const signInWithEmailPassword = async (email, password) => {
  try {
    await setupPersistence();
    
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  } catch (error) {
    console.error('Email Sign In Error:', error);
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    const actionCodeSettings = {
      url: 'https://saleswit.mgimpacts.com/login', // Update this for production
      handleCodeInApp: true,
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    return true;
  } catch (error) {
    console.error('Password Reset Error:', error);
    throw error;
  }
};

// Email Link Authentication
export const sendEmailSignInLink = async (email) => {
  try {
    const actionCodeSettings = {
      url: window.location.href,
      handleCodeInApp: true
    };
    
    localStorage.setItem('emailForSignIn', email);
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    return true;
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    throw error;
  }
};

// Handle email link sign-in
export const handleEmailLinkSignIn = async () => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    let savedEmail = localStorage.getItem('emailForSignIn');
    let savedName = localStorage.getItem('nameForSignIn') || '';
    
    if (!savedEmail) {
      savedEmail = window.prompt('Please provide your email for confirmation');
    }
    
    if (savedEmail) {
      try {
        const result = await signInWithEmailLink(auth, savedEmail, window.location.href);
        
        localStorage.removeItem('emailForSignIn');
        localStorage.removeItem('nameForSignIn');
        
        // Remove the email link parameters from URL
        window.history.replaceState(null, null, window.location.pathname);
        return result;
      } catch (error) {
        console.error('Error signing in with email link:', error);
        throw error;
      }
    }
  }
  return null;
};

// Reauthenticate user before sensitive operations like changing password
export const reauthenticateUser = async (user, currentPassword) => {
  try {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    return true;
  } catch (error) {
    console.error('Reauthentication Error:', error);
    throw error;
  }
};

export const changePassword = async (user, newPassword) => {
  try {
    await updatePassword(user, newPassword);
    return true;
  } catch (error) {
    console.error('Change Password Error:', error);
    throw error;
  }
};